import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { account as seedAccount, notifications as seedNotifications } from '../data/account';
import { campusById } from '../data/institutions';
import { DEFAULT_SIGNALS } from '../utils/recommend';
import type { SignalKey, SignalState } from '../types/intelligence';
import type { ConsoleKey, PlatformNotification, Role, StudentAccount, VisibilityLevel, VisibilitySurface } from '../types/identity';
export interface ConsoleDefinition {
  key: ConsoleKey;
  label: string;
  scope: string;
  path: string;
  role: Role;
}
export const CONSOLES: ConsoleDefinition[] = [{
  key: 'student',
  label: 'Student',
  scope: 'Amarachi Obi · UNILAG Akoka',
  path: '/',
  role: 'student'
}, {
  key: 'property',
  label: 'Property operator',
  scope: 'Cedarwood Living · 3 properties',
  path: '/property',
  role: 'property_manager'
}, {
  key: 'institution',
  label: 'Institution',
  scope: 'University of Lagos · Registry',
  path: '/institution',
  role: 'university_admin'
}, {
  key: 'provider',
  label: 'Service provider',
  scope: 'Marketplace · @amarachi',
  path: '/provider',
  role: 'service_provider'
}, {
  key: 'author',
  label: 'Author / publisher',
  scope: 'Knowledge marketplace',
  path: '/author',
  role: 'author'
}, {
  key: 'employer',
  label: 'Employer',
  scope: 'Havazon Works · recruitment',
  path: '/employer',
  role: 'employer'
}, {
  key: 'representative',
  label: 'Campus representative',
  scope: 'CR-UNILAG-014 · Akoka',
  path: '/representative',
  role: 'campus_representative'
}, {
  key: 'merchant',
  label: 'Merchant',
  scope: 'Campus Books · CampaGift',
  path: '/merchant',
  role: 'service_provider'
}, {
  key: 'admin',
  label: 'Campuzon admin',
  scope: 'Platform operations',
  path: '/admin',
  role: 'campuzon_admin'
}];
interface AccountContextValue {
  account: StudentAccount;
  institutionName: string;
  campusName: string;
  notifications: PlatformNotification[];
  unreadCount: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  setVisibility: (surface: VisibilitySurface, level: VisibilityLevel) => void;
  reports: string[];
  addReport: (subject: string) => void;
  blocked: string[];
  toggleBlock: (subject: string) => void;
  signals: SignalState;
  toggleSignal: (key: SignalKey) => void;
}
const AccountContext = createContext<AccountContextValue | null>(null);
export function AccountProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [account, setAccount] = useState<StudentAccount>(seedAccount);
  const [notifications, setNotifications] = useState<PlatformNotification[]>(seedNotifications);
  const [reports, setReports] = useState<string[]>([]);
  const [blocked, setBlocked] = useState<string[]>([]);
  const [signals, setSignals] = useState<SignalState>(DEFAULT_SIGNALS);
  const place = campusById(account.campusId);
  const markAllRead = useCallback(() => {
    setNotifications(current => current.map(item => ({
      ...item,
      read: true
    })));
  }, []);
  const markRead = useCallback((id: string) => {
    setNotifications(current => current.map(item => item.id === id ? {
      ...item,
      read: true
    } : item));
  }, []);
  const setVisibility = useCallback((surface: VisibilitySurface, level: VisibilityLevel) => {
    setAccount(current => ({
      ...current,
      visibility: {
        ...current.visibility,
        [surface]: level
      }
    }));
  }, []);
  const addReport = useCallback((subject: string) => {
    setReports(current => current.includes(subject) ? current : [...current, subject]);
  }, []);
  const toggleBlock = useCallback((subject: string) => {
    setBlocked(current => current.includes(subject) ? current.filter(item => item !== subject) : [...current, subject]);
  }, []);
  const toggleSignal = useCallback((key: SignalKey) => {
    setSignals(current => ({
      ...current,
      [key]: !current[key]
    }));
  }, []);
  const value = useMemo<AccountContextValue>(() => ({
    account,
    institutionName: place?.institution.name ?? 'Unknown institution',
    campusName: place?.campus.name ?? 'Unknown campus',
    notifications,
    unreadCount: notifications.filter(item => !item.read).length,
    markAllRead,
    markRead,
    setVisibility,
    reports,
    addReport,
    blocked,
    toggleBlock,
    signals,
    toggleSignal
  }), [account, place, notifications, markAllRead, markRead, setVisibility, reports, addReport, blocked, toggleBlock, signals, toggleSignal]);
  return <AccountContext.Provider value={value} data-id="element-5383" data-mp="pt2i38eld4c9">{children}</AccountContext.Provider>;
}
export function useAccount(): AccountContextValue {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used inside AccountProvider');
  }
  return context;
}